/**
 * The figures a part states only while it is in a given state.
 *
 * ITS OWN PANEL, below the attributes table rather than woven into it, and
 * that is a decision rather than a layout convenience. The attributes table
 * sends CHANGED FIELDS through the generic entity update; this sends a WHOLE
 * LIST through a PUT of its own. Sharing one dirty state between two write
 * paths with different semantics would make "save" mean two things, and a
 * partial failure would leave the reader unable to tell which half landed.
 *
 * WHAT IS NOT HERE IS THE BASELINE. That is the attribute's own value, edited
 * in the table above. Repeating it would give a reader two places to look for
 * one figure, and two places to change it.
 *
 * UNSET IS NOT ZERO, all the way up. A part genuinely powered DOWN in a state
 * has to state an explicit `0` here; leaving the row out falls back to the
 * baseline, which is the opposite claim.
 */

import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import { ApiError, api } from '../../api'
import type { EntityRow, StateValue } from '../../schema'
import { stateLabels } from '../common/state-names'
import ElementPicker from '../common/ElementPicker'

/**
 * The states this part may state a figure in.
 *
 * NARROWED TO THE STATES THE PART CAN BE IN (issue #42) - exhibited by it or by
 * anything it sits inside. The server works that out (`PartDetail.states`):
 * the chain is not an owner chain, because a definition declared at package
 * level has no owner and is placed by its USAGES, so the client does not have
 * what it would need to walk it. Before that list existed this deliberately
 * did not narrow at all, since a filter on the one level of `owner` the payload
 * carried hid every state from Seeker, the part the feature was built for.
 *
 * `allowed` absent means an older server: no narrowing, the old behaviour.
 *
 * Machines are excluded either way: a machine is the SET of states, not one
 * the part is in, so keying a value by it keys it by all of them at once.
 */
export function statesFor(states: EntityRow[], allowed?: string[]): EntityRow[] {
  const owners = new Set(states.map((row) => row.owner).filter(Boolean).map(String))
  const permitted = allowed ? new Set(allowed) : null
  return states.filter((state) => !owners.has(state.id) && (!permitted || permitted.has(state.id)))
}

// Moved to common/state-names so the transition editor names states the same
// way; re-exported because this panel's tests pin it here.
export { stateLabels }

/**
 * The choices a row's state picker offers: identifier and label.
 *
 * KEYED BY IDENTIFIER, because that is what the attributes payload names each
 * state by. The picker used to offer qualified names, so a row loaded from the
 * server matched no option and rendered blank - and saving sent the
 * identifier back in a form the write could not resolve, dropping the row.
 *
 * A state a row ALREADY names is always offered, even when the list would not
 * otherwise include it: hiding it would leave that row uneditable.
 */
export function pickerOptions(
  offered: EntityRow[],
  all: EntityRow[],
  values: StateValue[],
): { id: string; label: string }[] {
  const labels = stateLabels(all)
  const ids = offered.map((row) => row.id)
  for (const entry of values) {
    if (entry.state && !ids.includes(entry.state)) ids.push(entry.state)
  }
  return ids.map((id) => ({ id, label: labels.get(id) ?? id }))
}

/** Which rows cannot be saved, by index, and why. */
export function problems(values: StateValue[]): Map<number, string> {
  const found = new Map<number, string>()
  const seen = new Set<string>()
  values.forEach((entry, index) => {
    if (!entry.state.trim()) found.set(index, 'names no state')
    else if (!entry.value.trim() && !entry.budget.trim()) {
      found.set(index, 'states neither a value nor a budget')
    } else if (seen.has(entry.state)) {
      // The overlay is unique on (part, feature, state), so a second row for
      // one state is not a second figure - it is one figure and a lost
      // statement.
      found.set(index, 'repeats a state already listed')
    }
    seen.add(entry.state)
  })
  return found
}

const BLANK: StateValue = { state: '', value: '', budget: '', basis: '' }

export default function StateValuesPanel({
  identifier,
  feature,
  label,
  unit,
  states,
  allowed,
  values,
  onSaved,
}: {
  identifier: string
  feature: string
  label: string
  unit: string
  /** EVERY state the model lists, machines included - the labels need owners. */
  states: EntityRow[]
  /** The identifiers this part can be in (`PartDetail.states`); absent = all. */
  allowed?: string[]
  values: StateValue[]
  onSaved: () => void
}) {
  const [edits, setEdits] = useState<StateValue[] | null>(null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const editing = edits !== null
  const rows = edits ?? values
  const broken = editing ? problems(rows) : new Map<number, string>()
  const offered = statesFor(states, allowed)
  const labels = stateLabels(states)
  const options = pickerOptions(offered, states, rows)

  const change = (index: number, key: keyof StateValue, value: string) => {
    setEdits((current) =>
      (current ?? []).map((row, at) => (at === index ? { ...row, [key]: value } : row)))
  }

  const save = () => {
    setSaving(true)
    setError('')
    api
      .setPartStateValues(identifier, feature, rows)
      .then(() => {
        setEdits(null)
        onSaved()
      })
      .catch((failure: ApiError) => setError(failure.message))
      .finally(() => setSaving(false))
  }

  if (offered.length === 0 && values.length === 0) {
    // NOT AN EMPTY EDITOR. With no states declared there is nothing to key a
    // figure by, and offering the control would be offering something that can
    // only fail. The ordinary case for BALROG today.
    // Two different reasons, and they send the reader to different places: a
    // model with no states at all, or states that belong to other parts.
    return (
      <Typography variant="body2" color="text.secondary">
        {states.length === 0
          ? 'The model declares no states, so there is nothing to qualify a value by.'
          : 'No state applies to this part: none is exhibited by it or by anything it sits inside.'}
      </Typography>
    )
  }

  return (
    <Stack spacing={1}>
      {error && <Alert severity="error">{error}</Alert>}

      {rows.length === 0 && !editing ? (
        <Typography variant="body2" color="text.secondary">
          {label} is {unit ? `stated once, in ${unit}` : 'stated once'}, and holds in every state.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>In state</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Budget</TableCell>
              <TableCell>Basis</TableCell>
              {editing && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index}>
                <TableCell>
                  {editing ? (
                    <ElementPicker
                      options={options.map((option) => ({ value: option.id, label: option.label }))}
                      show="label"
                      value={row.state || null}
                      onChange={(next) => change(index, 'state', next)}
                      ariaLabel={`In state ${index + 1}`}
                      variant="standard"
                      error={broken.has(index)}
                      sx={{ minWidth: 150 }}
                    />
                  ) : (
                    labels.get(row.state) ?? row.state
                  )}
                </TableCell>
                {(['value', 'budget', 'basis'] as (keyof StateValue)[]).map((key) => (
                  <TableCell key={key}>
                    {editing ? (
                      <TextField
                        size="small"
                        variant="standard"
                        value={String(row[key] ?? '')}
                        placeholder={key === 'basis' ? '' : unit}
                        onChange={(event) => change(index, key, event.target.value)}
                        sx={{ minWidth: 90 }}
                      />
                    ) : (
                      String(row[key] || '-')
                    )}
                  </TableCell>
                ))}
                {editing && (
                  <TableCell padding="none">
                    <IconButton
                      size="small"
                      aria-label={`Remove state value ${index + 1}`}
                      onClick={() =>
                        setEdits((current) => (current ?? []).filter((_, at) => at !== index))
                      }
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {broken.size > 0 && (
        <Alert severity="warning">
          {[...broken.entries()]
            .map(([index, why]) => `Row ${index + 1} ${why}`)
            .join('; ')}
        </Alert>
      )}

      {editing ? (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            onClick={() => setEdits((current) => [...(current ?? []), { ...BLANK }])}
          >
            Add a state
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" onClick={() => setEdits(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={save}
            disabled={saving || broken.size > 0}
          >
            Save
          </Button>
        </Stack>
      ) : (
        <Box>
          <Button size="small" onClick={() => setEdits(values.map((row) => ({ ...row })))}>
            {values.length ? 'Edit state values' : 'State a value per state'}
          </Button>
        </Box>
      )}
    </Stack>
  )
}
